// Settings → Models: the model each step uses by default, for the whole account or one project, each with its
// price per 5 s (or per item) and what it won't do. The Director can do the same by voice ("always use Kling").
import { useState } from "react";
import { api } from "../api";
import { director } from "../director";
import { choiceSource, MODEL_STEPS, modelLabel, priceLabel, resolveModel, STEP_LABEL } from "../models";
import { Link } from "../router";
import { toast } from "../store";
import type { ModelInfo, ModelStep, MyModels, Project } from "../types";
import { Icon, Sheet, Spinner, useLoad } from "./bits";
import { ModelOptions, useModels } from "./ModelPicker";
import { useImageShowcase } from "./ImageShowcase";
import { rowForProject } from "../showcaseImages";

export function ModelDefaults() {
  const models = useModels();
  const { data: mine, setData: setMine } = useLoad<MyModels | null>(() => api.myModels().catch(() => null), []);
  const { data: projects, setData: setProjects } = useLoad<Project[]>(() => api.projects().catch(() => []), []);
  const [scope, setScope] = useState<string>("account");
  const [step, setStep] = useState<ModelStep | null>(null);
  const [busy, setBusy] = useState(false);
  const project = projects?.find((p) => p.id === scope);
  // MS-IMG: picture models show their showcase stills, the style that fits this project first
  const stills = useImageShowcase();

  const choice = { project: project?.modelPrefs, account: mine?.account, defaults: mine?.defaults };
  const pick = async (s: ModelStep, m: ModelInfo) => {
    setBusy(true);
    try {
      if (project) {
        await api.setProjectModel(project.id, s, m.id);
        setProjects((projects ?? []).map((p) => (p.id === project.id ? { ...p, modelPrefs: { ...p.modelPrefs, [s]: m.id } } : p)));
        director.context(`The user set ${STEP_LABEL[s].toLowerCase()} on "${project.title}" to ${m.label} in Settings.`);
      } else {
        await api.setMyModel(s, m.id);
        setMine({ defaults: mine?.defaults ?? {}, account: { ...mine?.account, [s]: m.id } });
        director.context(`The user made ${m.label} their default for ${STEP_LABEL[s].toLowerCase()} in Settings.`);
      }
      toast(`${m.label} for ${STEP_LABEL[s].toLowerCase()}${project ? ` on ${project.title}` : " from now on"}.`, "good");
      setStep(null);
    } catch (e) {
      toast(`Couldn't change it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };

  // the API clears a choice when sent "default"
  const reset = async (s: ModelStep) => {
    setBusy(true);
    try {
      if (project) {
        await api.setProjectModel(project.id, s, "default");
        const prefs = { ...project.modelPrefs };
        delete prefs[s];
        setProjects((projects ?? []).map((p) => (p.id === project.id ? { ...p, modelPrefs: prefs } : p)));
      } else {
        await api.setMyModel(s, "default");
        const account = { ...mine?.account };
        delete account[s];
        setMine({ defaults: mine?.defaults ?? {}, account });
      }
      director.context(`The user reset ${STEP_LABEL[s].toLowerCase()}${project ? ` on "${project.title}"` : ""} to the default in Settings.`);
      setStep(null);
    } catch (e) {
      toast(`Couldn't change it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="group model-defaults" aria-labelledby="models-h">
      <h2 id="models-h" className="group-title">
        Models
      </h2>
      <p className="muted">
        What each step is made with. Your Director tells you the price difference before it switches, and you can still pick per scene. <Link to="/models">Compare every model</Link>
      </p>
      <label className="select">
        <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Which defaults">
          <option value="account">Every new video (your account)</option>
          {(projects ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              Just “{p.title}”
            </option>
          ))}
        </select>
      </label>
      {!models && <Spinner label="Loading the models" />}
      {models && (
        <ul className="md-rows">
          {MODEL_STEPS.map((s) => {
            const id = resolveModel(s, choice, models);
            const m = models.find((x) => x.id === id);
            const src = choiceSource(s, choice);
            return (
              <li key={s}>
                <button data-action="models.step" type="button" className="md-row" onClick={() => setStep(s)}>
                  <span className="md-step">{STEP_LABEL[s]}</span>
                  <span className="md-pick">
                    <strong>{m?.label ?? modelLabel(id, models)}</strong>
                    <span className="muted">
                      {m ? priceLabel(m) : ""}
                      {src === "default" ? ", the default" : src === "project" && !project ? "" : src === "account" && project ? ", from your account" : ""}
                    </span>
                    {m?.constraints.map((c) => (
                      <span key={c} className="model-constraint">
                        <Icon name="alert" size={13} /> {c.replace(/^./, (x) => x.toUpperCase())}
                      </span>
                    ))}
                  </span>
                  <span className="md-change">Change</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {step && models && (
        <Sheet title={STEP_LABEL[step]} onClose={() => setStep(null)}>
          <div className="sheet-body">
            <p className="muted">{project ? `For “${project.title}” only.` : "For every new video. A project or a scene can still use something else."}</p>
            <ModelOptions step={step} models={models} value={resolveModel(step, choice, models)} onPick={(m) => void pick(step, m)} busy={busy} durationS={step === "clip" ? 5 : undefined} stills={{ data: stills, row: rowForProject(project) }} />
            {(project ? project.modelPrefs[step] : mine?.account[step]) && (
              <button data-action="models.step" type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={() => void reset(step)}>
                {project ? "Use the account's choice" : "Use the default"}
              </button>
            )}
          </div>
        </Sheet>
      )}
    </section>
  );
}
